import { supabase } from '@/core/infrastructure/supabase/supabaseClient';

// ==============================================================================
// TIPOS DEL DOMINIO — EQUIPOS CLÍNICOS (Escenario 2)
// Ref: docs/02_ESTANDARES_INGENIERIA/06_ESCENARIO_2_EQUIPOS_CLINICOS.md
// ==============================================================================

export interface EquipoResumen {
  equipo_uuid: string;
  nombre_display: string;
  creado_por_uuid: string;
  miembros?: EquipoMiembro[];
}

export interface EquipoMiembro {
  miembro_uuid: string;
  nombre_display: string;  // Nombre del perfil
  initials: string;        // Para el Avatar (ej. "JR" para Juan Rodríguez)
  avatar_url?: string;     // Foto de perfil
  rol_equipo: 'clinico_principal' | 'clinico_asistente' | 'secretaria';
}

/** Cita de equipo enriquecida con los asistentes y el nombre del equipo */
export interface EquipoCita {
  cita_equipo_id: string;
  equipo_uuid: string;
  equipo_nombre: string;
  paciente_uuid: string;
  paciente_nombre: string;
  fecha_hora_inicio: string;  // ISO 8601 UTC
  fecha_hora_fin: string;     // ISO 8601 UTC
  estado: string;
  asistentes: EquipoMiembro[];
}

// ==============================================================================
// SERVICIO: fetchMisEquipos
// Retorna todos los equipos a los que la sesión activa pertenece (como miembro).
// RLS se encarga del filtrado Zero-Trust en el backend.
// ==============================================================================
export async function fetchMisEquipos(): Promise<EquipoResumen[]> {
  const { data, error } = await supabase
    .from('equipos_clinicos')
    .select(`
      equipo_uuid,
      nombre_display,
      creado_por_uuid,
      equipo_miembros (
        miembro_uuid,
        rol_equipo,
        estado,
        perfiles_usuario:miembro_uuid (
          nombres_completos, apellidos_completos, avatar_url
        )
      )
    `);

  if (error) {
    console.error('[TeamCalendarService] fetchMisEquipos error:', error.message);
    return [];
  }

  // Mapear al tipo limpio
  return (data ?? []).map((eq: {
    equipo_uuid: string;
    nombre_display: string;
    creado_por_uuid: string;
    equipo_miembros: unknown;
  }) => {
    const miembros: EquipoMiembro[] = ((eq.equipo_miembros as unknown as Array<{
      miembro_uuid: string;
      rol_equipo: string;
      estado: string;
      perfiles_usuario: { nombres_completos: string, apellidos_completos: string, avatar_url: string | null } | null;
    }>) ?? []).filter((m) => m.estado === 'activo').map((m) => {
      const p = m.perfiles_usuario;
      const nombre = (p ? `${p.nombres_completos || ''} ${p.apellidos_completos || ''}`.trim() : '') || 'Sin nombre';
      const parts = nombre.trim().split(' ');
      const initials = parts.length >= 2
        ? `${parts[0][0]}${parts[1][0]}`.toUpperCase()
        : nombre.substring(0, 2).toUpperCase();

      return {
        miembro_uuid: m.miembro_uuid,
        nombre_display: nombre,
        initials,
        avatar_url: p?.avatar_url || undefined,
        rol_equipo: m.rol_equipo as EquipoMiembro['rol_equipo'],
      };
    });

    return {
      equipo_uuid: eq.equipo_uuid,
      nombre_display: eq.nombre_display,
      creado_por_uuid: eq.creado_por_uuid,
      miembros,
    };
  });
}

// ==============================================================================
// SERVICIO: fetchCitasEquipo
// Obtiene las citas de todos los equipos accesibles, filtradas por fecha.
// Devuelve citas enriquecidas con asistentes y nombre del equipo.
// ==============================================================================
export async function fetchCitasEquipo(fecha: string): Promise<EquipoCita[]> {
  // Construir rango UTC del día
  const startOfDay = `${fecha}T00:00:00.000Z`;
  const endOfDay   = `${fecha}T23:59:59.999Z`;

  const { data, error } = await supabase
    .from('agendamiento_equipo')
    .select(`
      cita_equipo_id,
      equipo_uuid,
      paciente_uuid,
      fecha_hora_inicio,
      fecha_hora_fin,
      estado,
      equipos_clinicos:equipo_uuid (
        nombre_display
      ),
      paciente:paciente_uuid (
        nombres_completos, apellidos_completos
      ),
      cita_equipo_asistencias (
        miembro_uuid,
        perfiles_usuario:miembro_uuid (
          nombres_completos, apellidos_completos
        )
      )
    `)
    .gte('fecha_hora_inicio', startOfDay)
    .lte('fecha_hora_inicio', endOfDay)
    .order('fecha_hora_inicio', { ascending: true });

  if (error) {
    console.error('[TeamCalendarService] fetchCitasEquipo error:', error.message);
    return [];
  }

  return (data ?? []).map((row: {
    cita_equipo_id: string;
    equipo_uuid: string;
    paciente_uuid: string;
    fecha_hora_inicio: string;
    fecha_hora_fin: string;
    estado: string;
    equipos_clinicos: unknown;
    paciente: unknown;
    cita_equipo_asistencias: unknown;
  }) => {
    const asistentesRaw = (row.cita_equipo_asistencias as unknown as Array<{
      miembro_uuid: string;
      perfiles_usuario: { nombres_completos: string, apellidos_completos: string } | null;
    }>) ?? [];

    const asistentes: EquipoMiembro[] = asistentesRaw.map((a) => {
      const p = a.perfiles_usuario;
      const nombre = (p ? `${p.nombres_completos || ''} ${p.apellidos_completos || ''}`.trim() : '') || 'Sin nombre';
      const parts = nombre.trim().split(' ');
      const initials = parts.length >= 2
        ? `${parts[0][0]}${parts[1][0]}`.toUpperCase()
        : nombre.substring(0, 2).toUpperCase();
      return {
        miembro_uuid: a.miembro_uuid,
        nombre_display: nombre,
        initials,
        rol_equipo: 'clinico_asistente' as EquipoMiembro['rol_equipo'],
      };
    });

    const equipoData = row.equipos_clinicos as unknown as { nombre_display: string } | null;
    const pacienteData = row.paciente as unknown as { nombre_completo: string } | null;

    return {
      cita_equipo_id: row.cita_equipo_id,
      equipo_uuid: row.equipo_uuid,
      equipo_nombre: equipoData?.nombre_display ?? 'Equipo',
      paciente_uuid: row.paciente_uuid,
      paciente_nombre: pacienteData?.nombre_completo ?? 'Paciente',
      fecha_hora_inicio: row.fecha_hora_inicio,
      fecha_hora_fin: row.fecha_hora_fin,
      estado: row.estado,
      asistentes,
    };
  });
}

// ==============================================================================
// SERVICIO: crearCitaEquipo
// Inserta una nueva cita en agendamiento_equipo y las asistencias N:M.
// ==============================================================================
export interface NuevaCitaEquipoPayload {
  equipo_uuid: string;
  paciente_uuid: string;
  fecha_hora_inicio: string;  // ISO UTC
  fecha_hora_fin: string;     // ISO UTC
  asistente_uuids: string[];  // Los miembros que asistirán
}

export async function crearCitaEquipo(
  payload: NuevaCitaEquipoPayload
): Promise<{ success: boolean; message?: string; cita_equipo_id?: string }> {
  try {
    // 1. Insertar la cita
    const { data: citaData, error: citaError } = await supabase
      .from('agendamiento_equipo')
      .insert({
        equipo_uuid: payload.equipo_uuid,
        paciente_uuid: payload.paciente_uuid,
        fecha_hora_inicio: payload.fecha_hora_inicio,
        fecha_hora_fin: payload.fecha_hora_fin,
        estado: 'confirmada',
      })
      .select('cita_equipo_id')
      .single();

    if (citaError || !citaData) {
      return { success: false, message: citaError?.message ?? 'Error al crear la cita de equipo.' };
    }

    // 2. Insertar las asistencias N:M
    if (payload.asistente_uuids.length > 0) {
      const asistencias = payload.asistente_uuids.map((uuid) => ({
        cita_equipo_id: citaData.cita_equipo_id,
        miembro_uuid: uuid,
      }));

      const { error: asistError } = await supabase
        .from('cita_equipo_asistencias')
        .insert(asistencias);

      if (asistError) {
        console.warn('[TeamCalendarService] Cita creada pero error en asistencias:', asistError.message);
      }
    }

    return { success: true, cita_equipo_id: citaData.cita_equipo_id };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Error desconocido';
    return { success: false, message };
  }
}

// ==============================================================================
// SERVICIO: reagendarCitaEquipo
// Actualiza el bloque de tiempo de una cita de equipo.
// ==============================================================================
export async function reagendarCitaEquipo(
  citaEquipoId: string,
  nuevaFechaInicio: string,
  nuevaFechaFin: string
): Promise<{ success: boolean; message?: string }> {
  const { error } = await supabase
    .from('agendamiento_equipo')
    .update({
      fecha_hora_inicio: nuevaFechaInicio,
      fecha_hora_fin: nuevaFechaFin,
    })
    .eq('cita_equipo_id', citaEquipoId);

  if (error) {
    return { success: false, message: error.message };
  }
  return { success: true };
}

// ==============================================================================
// SERVICIO: updateEstadoCitaEquipo
// Cambia el estado de una cita de equipo (cancelar, completar, etc).
// Para cancelar/eliminar definitivamente, lo eliminamos físicamente (es una cita manual).
// ==============================================================================
export async function updateEstadoCitaEquipo(
  citaEquipoId: string,
  nuevoEstado: string
): Promise<{ success: boolean; message?: string }> {
  // Para cancelar, eliminamos el registro (es un evento manual, no digital)
  if (nuevoEstado === 'cancelada_paciente') {
    const { error } = await supabase
      .from('agendamiento_equipo')
      .delete()
      .eq('cita_equipo_id', citaEquipoId);

    if (error) return { success: false, message: error.message };
    return { success: true };
  }

  const { error } = await supabase
    .from('agendamiento_equipo')
    .update({ estado: nuevoEstado })
    .eq('cita_equipo_id', citaEquipoId);

  if (error) return { success: false, message: error.message };
  return { success: true };
}

// ==============================================================================
// SERVICIO: updateAsistenciasCita
// Reemplaza atómicamente la lista de asistentes de una cita de equipo.
// Operación: DELETE todos los registros existentes + INSERT los nuevos.
// ==============================================================================
export async function updateAsistenciasCita(
  citaEquipoId: string,
  asistenteUuids: string[]
): Promise<{ success: boolean; message?: string }> {
  try {
    // 1. Eliminar asistencias actuales
    const { error: deleteError } = await supabase
      .from('cita_equipo_asistencias')
      .delete()
      .eq('cita_equipo_id', citaEquipoId);

    if (deleteError) {
      return { success: false, message: deleteError.message };
    }

    // 2. Insertar los nuevos asistentes (si hay al menos uno)
    if (asistenteUuids.length > 0) {
      const rows = asistenteUuids.map((uuid) => ({
        cita_equipo_id: citaEquipoId,
        miembro_uuid: uuid,
      }));

      const { error: insertError } = await supabase
        .from('cita_equipo_asistencias')
        .insert(rows);

      if (insertError) {
        return { success: false, message: insertError.message };
      }
    }

    return { success: true };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Error desconocido';
    return { success: false, message };
  }
}

// ==============================================================================
// SERVICIO: checkConflictosEquipo
// Llama al RPC fn_check_conflictos_equipo para determinar si hay un Hard Block
// o un Soft Warning cruzado entre las agendas de los médicos.
// ==============================================================================
export async function checkConflictosEquipo(
  profesionalesUuids: string[],
  fechaHoraInicio: string,
  fechaHoraFin: string
): Promise<{ success: boolean; conflictos?: any[]; message?: string }> {
  try {
    const { data, error } = await supabase.rpc('fn_check_conflictos_equipo', {
      p_profesionales_uuids: profesionalesUuids,
      p_fecha_hora_inicio: fechaHoraInicio,
      p_fecha_hora_fin: fechaHoraFin
    });

    if (error) {
      console.error('[TeamCalendarService] Error al revisar conflictos:', error.message);
      return { success: false, message: error.message };
    }

    return { success: true, conflictos: data || [] };
  } catch (err: any) {
    return { success: false, message: err.message };
  }
}
